import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, RefreshCw, Settings, Brain, Zap, BarChart3, AlertTriangle, CheckCircle, TrendingUp, Activity } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import AIConfigurationService from "@/api/aiConfiguration.service";

interface AIConfiguration {
  id: number;
  businessId: number;
  modelProvider: 'openai' | 'anthropic' | 'google' | 'azure';
  modelName: string;
  apiKey?: string;
  apiEndpoint?: string;
  confidenceThreshold: number;
  maxRetries: number;
  timeoutSeconds: number;
  extractionRules: {
    enableOCR: boolean;
    ocrProvider: string;
    enableNLP: boolean;
    nlpProvider: string;
    customRules: any[];
  };
  validationRules: {
    enableAmountValidation: boolean;
    enableDateValidation: boolean;
    enableVendorValidation: boolean;
    customValidations: any[];
  };
  performanceSettings: {
    batchSize: number;
    parallelProcessing: boolean;
    cacheResults: boolean;
    cacheTTLHours: number;
  };
  isActive: boolean;
  dateCreated: string;
  lastUpdated: string;
}

interface AIMetrics {
  totalProcessed: number;
  successRate: number;
  averageConfidence: number;
  averageProcessingTime: number;
  errorRate: number;
  dailyVolume: number[];
  accuracyTrend: number[];
  performanceMetrics: {
    throughput: number;
    latency: number;
    errorCount: number;
    retryCount: number;
  };
}

export default function AIConfiguration() {
  const [configuration, setConfiguration] = useState<AIConfiguration | null>(null);
  const [metrics, setMetrics] = useState<AIMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [currentTab, setCurrentTab] = useState("general");

  // Form state
  const [formData, setFormData] = useState({
    modelProvider: "openai",
    modelName: "gpt-4",
    apiKey: "",
    apiEndpoint: "",
    confidenceThreshold: 85,
    maxRetries: 3,
    timeoutSeconds: 30,
    enableOCR: true,
    ocrProvider: "tesseract",
    enableNLP: true,
    nlpProvider: "spacy",
    enableAmountValidation: true,
    enableDateValidation: true,
    enableVendorValidation: true,
    batchSize: 10,
    parallelProcessing: true,
    cacheResults: true,
    cacheTTLHours: 24,
    isActive: true
  });

  const { toast } = useToast();

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    if (selectedBusiness) {
      fetchConfiguration();
      fetchMetrics();
    }
  }, [selectedBusiness]);

  const fetchConfiguration = async () => {
    if (!selectedBusiness) return;
    
    try {
      setLoading(true);
      const response = await AIConfigurationService.getAIConfiguration(selectedBusiness.id);
      if (response.data.result === "success" && response.data.configuration) {
        const config = response.data.configuration;
        setConfiguration(config);
        
        // Update form data
        setFormData({
          modelProvider: config.modelProvider,
          modelName: config.modelName,
          apiKey: config.apiKey || "",
          apiEndpoint: config.apiEndpoint || "",
          confidenceThreshold: config.confidenceThreshold,
          maxRetries: config.maxRetries,
          timeoutSeconds: config.timeoutSeconds,
          enableOCR: config.extractionRules.enableOCR,
          ocrProvider: config.extractionRules.ocrProvider,
          enableNLP: config.extractionRules.enableNLP,
          nlpProvider: config.extractionRules.nlpProvider,
          enableAmountValidation: config.validationRules.enableAmountValidation,
          enableDateValidation: config.validationRules.enableDateValidation,
          enableVendorValidation: config.validationRules.enableVendorValidation,
          batchSize: config.performanceSettings.batchSize,
          parallelProcessing: config.performanceSettings.parallelProcessing,
          cacheResults: config.performanceSettings.cacheResults,
          cacheTTLHours: config.performanceSettings.cacheTTLHours,
          isActive: config.isActive
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch AI configuration",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchMetrics = async () => {
    if (!selectedBusiness) return;
    
    try {
      const response = await AIConfigurationService.getAIMetrics(selectedBusiness.id);
      if (response.data.result === "success") {
        setMetrics(response.data.metrics);
      }
    } catch (error) {
      console.error("Failed to fetch AI metrics:", error);
    }
  };

  const handleSave = async () => {
    if (!selectedBusiness) return;

    try {
      setSaving(true);
      
      const configData = {
        businessId: selectedBusiness.id,
        modelProvider: formData.modelProvider,
        modelName: formData.modelName,
        apiKey: formData.apiKey,
        apiEndpoint: formData.apiEndpoint,
        confidenceThreshold: formData.confidenceThreshold,
        maxRetries: formData.maxRetries,
        timeoutSeconds: formData.timeoutSeconds,
        extractionRules: {
          enableOCR: formData.enableOCR,
          ocrProvider: formData.ocrProvider,
          enableNLP: formData.enableNLP,
          nlpProvider: formData.nlpProvider,
          customRules: []
        },
        validationRules: {
          enableAmountValidation: formData.enableAmountValidation,
          enableDateValidation: formData.enableDateValidation,
          enableVendorValidation: formData.enableVendorValidation,
          customValidations: []
        },
        performanceSettings: {
          batchSize: formData.batchSize,
          parallelProcessing: formData.parallelProcessing,
          cacheResults: formData.cacheResults,
          cacheTTLHours: formData.cacheTTLHours
        },
        isActive: formData.isActive
      };

      if (configuration) {
        await AIConfigurationService.updateAIConfiguration(configuration.id, configData);
        toast({
          title: "Success",
          description: "AI configuration updated successfully",
        });
      } else {
        await AIConfigurationService.createAIConfiguration(configData);
        toast({
          title: "Success",
          description: "AI configuration created successfully",
        });
      }
      
      fetchConfiguration();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save AI configuration",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTestingConnection(true);
      const response = await AIConfigurationService.testAIConnection({
        modelProvider: formData.modelProvider,
        modelName: formData.modelName,
        apiKey: formData.apiKey,
        apiEndpoint: formData.apiEndpoint
      });
      
      if (response.data.result === "success") {
        toast({
          title: "Success",
          description: "AI connection test successful",
        });
      } else {
        toast({
          title: "Error",
          description: "AI connection test failed",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to test AI connection",
        variant: "destructive",
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <Badge variant={isActive ? "default" : "secondary"}>
        {isActive ? (
          <>
            <CheckCircle className="h-3 w-3 mr-1" />
            Active
          </>
        ) : (
          <>
            <AlertTriangle className="h-3 w-3 mr-1" />
            Inactive
          </>
        )}
      </Badge>
    );
  };

  const getPerformanceColor = (value: number, threshold: number) => {
    if (value >= threshold) return "text-green-600";
    if (value >= threshold * 0.7) return "text-yellow-600";
    return "text-red-600";
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI Configuration</h1>
          <p className="text-muted-foreground">
            Configure AI models and processing settings for document extraction
          </p>
        </div>
        <div className="flex items-center gap-2">
          {configuration && getStatusBadge(configuration.isActive)}
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Configuration
          </Button>
        </div>
      </div>

      {/* Metrics Overview */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Documents Processed</p>
                  <p className="text-2xl font-bold">{metrics.totalProcessed.toLocaleString()}</p>
                </div>
                <Activity className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
                  <p className={`text-2xl font-bold ${getPerformanceColor(metrics.successRate, 90)}`}>
                    {metrics.successRate.toFixed(1)}%
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Avg Confidence</p>
                  <p className={`text-2xl font-bold ${getPerformanceColor(metrics.averageConfidence, 85)}`}>
                    {metrics.averageConfidence.toFixed(1)}%
                  </p>
                </div>
                <Brain className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Avg Processing Time</p>
                  <p className="text-2xl font-bold">{metrics.averageProcessingTime.toFixed(1)}s</p>
                </div>
                <Zap className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Configuration Tabs */}
      <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="extraction">Extraction</TabsTrigger>
          <TabsTrigger value="validation">Validation</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Model Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="model-provider">Model Provider</Label>
                  <Select value={formData.modelProvider} onValueChange={(value) => setFormData({ ...formData, modelProvider: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="anthropic">Anthropic</SelectItem>
                      <SelectItem value="google">Google</SelectItem>
                      <SelectItem value="azure">Azure OpenAI</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="model-name">Model Name</Label>
                  <Select value={formData.modelName} onValueChange={(value) => setFormData({ ...formData, modelName: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.modelProvider === 'openai' && (
                        <>
                          <SelectItem value="gpt-4">GPT-4</SelectItem>
                          <SelectItem value="gpt-4-turbo">GPT-4 Turbo</SelectItem>
                          <SelectItem value="gpt-3.5-turbo">GPT-3.5 Turbo</SelectItem>
                        </>
                      )}
                      {formData.modelProvider === 'anthropic' && (
                        <>
                          <SelectItem value="claude-3-opus">Claude 3 Opus</SelectItem>
                          <SelectItem value="claude-3-sonnet">Claude 3 Sonnet</SelectItem>
                          <SelectItem value="claude-3-haiku">Claude 3 Haiku</SelectItem>
                        </>
                      )}
                      {formData.modelProvider === 'google' && (
                        <>
                          <SelectItem value="gemini-pro">Gemini Pro</SelectItem>
                          <SelectItem value="gemini-pro-vision">Gemini Pro Vision</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="api-key">API Key</Label>
                <Input
                  id="api-key"
                  type="password"
                  value={formData.apiKey}
                  onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                  placeholder="Enter your API key"
                />
              </div>
              <div>
                <Label htmlFor="api-endpoint">API Endpoint (Optional)</Label>
                <Input
                  id="api-endpoint"
                  value={formData.apiEndpoint}
                  onChange={(e) => setFormData({ ...formData, apiEndpoint: e.target.value })}
                  placeholder="Custom API endpoint URL"
                />
              </div>
              <div className="flex justify-end">
                <Button onClick={handleTestConnection} disabled={testingConnection || !formData.apiKey}>
                  {testingConnection ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Processing Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="confidence-threshold">
                  Confidence Threshold: {formData.confidenceThreshold}%
                </Label>
                <Slider
                  id="confidence-threshold"
                  min={50}
                  max={100}
                  step={5}
                  value={[formData.confidenceThreshold]}
                  onValueChange={(value) => setFormData({ ...formData, confidenceThreshold: value[0] })}
                  className="mt-2"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Documents with confidence below this threshold will be flagged for review
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="max-retries">Max Retries</Label>
                  <Input
                    id="max-retries"
                    type="number"
                    min="1"
                    max="10"
                    value={formData.maxRetries}
                    onChange={(e) => setFormData({ ...formData, maxRetries: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label htmlFor="timeout">Timeout (seconds)</Label>
                  <Input
                    id="timeout"
                    type="number"
                    min="10"
                    max="300"
                    value={formData.timeoutSeconds}
                    onChange={(e) => setFormData({ ...formData, timeoutSeconds: parseInt(e.target.value) })}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="is-active"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
                />
                <Label htmlFor="is-active">Enable AI Processing</Label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="extraction" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="h-5 w-5" />
                Extraction Rules
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="enable-ocr">Optical Character Recognition (OCR)</Label>
                    <p className="text-sm text-muted-foreground">Extract text from scanned documents and images</p>
                  </div>
                  <Switch
                    id="enable-ocr"
                    checked={formData.enableOCR}
                    onCheckedChange={(checked) => setFormData({ ...formData, enableOCR: checked })}
                  />
                </div>
                {formData.enableOCR && (
                  <div className="ml-4">
                    <Label htmlFor="ocr-provider">OCR Provider</Label>
                    <Select value={formData.ocrProvider} onValueChange={(value) => setFormData({ ...formData, ocrProvider: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select OCR provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tesseract">Tesseract</SelectItem>
                        <SelectItem value="google-vision">Google Vision API</SelectItem>
                        <SelectItem value="aws-textract">AWS Textract</SelectItem>
                        <SelectItem value="azure-cognitive">Azure Cognitive Services</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <Separator />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="enable-nlp">Natural Language Processing (NLP)</Label>
                    <p className="text-sm text-muted-foreground">Extract structured data from text using NLP</p>
                  </div>
                  <Switch
                    id="enable-nlp"
                    checked={formData.enableNLP}
                    onCheckedChange={(checked) => setFormData({ ...formData, enableNLP: checked })}
                  />
                </div>
                {formData.enableNLP && (
                  <div className="ml-4">
                    <Label htmlFor="nlp-provider">NLP Provider</Label>
                    <Select value={formData.nlpProvider} onValueChange={(value) => setFormData({ ...formData, nlpProvider: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select NLP provider" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="spacy">spaCy</SelectItem>
                        <SelectItem value="nltk">NLTK</SelectItem>
                        <SelectItem value="stanford-nlp">Stanford NLP</SelectItem>
                        <SelectItem value="huggingface">Hugging Face</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="validation" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Validation Rules
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="enable-amount-validation">Amount Validation</Label>
                    <p className="text-sm text-muted-foreground">Validate extracted amounts against business rules</p>
                  </div>
                  <Switch
                    id="enable-amount-validation"
                    checked={formData.enableAmountValidation}
                    onCheckedChange={(checked) => setFormData({ ...formData, enableAmountValidation: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="enable-date-validation">Date Validation</Label>
                    <p className="text-sm text-muted-foreground">Validate extracted dates for format and reasonableness</p>
                  </div>
                  <Switch
                    id="enable-date-validation"
                    checked={formData.enableDateValidation}
                    onCheckedChange={(checked) => setFormData({ ...formData, enableDateValidation: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="enable-vendor-validation">Vendor Validation</Label>
                    <p className="text-sm text-muted-foreground">Validate extracted vendor information against database</p>
                  </div>
                  <Switch
                    id="enable-vendor-validation"
                    checked={formData.enableVendorValidation}
                    onCheckedChange={(checked) => setFormData({ ...formData, enableVendorValidation: checked })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Performance Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="batch-size">Batch Size</Label>
                  <Input
                    id="batch-size"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.batchSize}
                    onChange={(e) => setFormData({ ...formData, batchSize: parseInt(e.target.value) })}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    Number of documents to process in parallel
                  </p>
                </div>
                <div>
                  <Label htmlFor="cache-ttl">Cache TTL (hours)</Label>
                  <Input
                    id="cache-ttl"
                    type="number"
                    min="1"
                    max="168"
                    value={formData.cacheTTLHours}
                    onChange={(e) => setFormData({ ...formData, cacheTTLHours: parseInt(e.target.value) })}
                  />
                  <p className="text-sm text-muted-foreground mt-1">
                    How long to cache processing results
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="parallel-processing">Parallel Processing</Label>
                    <p className="text-sm text-muted-foreground">Process multiple documents simultaneously</p>
                  </div>
                  <Switch
                    id="parallel-processing"
                    checked={formData.parallelProcessing}
                    onCheckedChange={(checked) => setFormData({ ...formData, parallelProcessing: checked })}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="cache-results">Cache Results</Label>
                    <p className="text-sm text-muted-foreground">Cache processing results to improve performance</p>
                  </div>
                  <Switch
                    id="cache-results"
                    checked={formData.cacheResults}
                    onCheckedChange={(checked) => setFormData({ ...formData, cacheResults: checked })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Performance Metrics */}
          {metrics && (
            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{metrics.performanceMetrics.throughput}</p>
                    <p className="text-sm text-muted-foreground">Docs/Hour</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{metrics.performanceMetrics.latency}ms</p>
                    <p className="text-sm text-muted-foreground">Avg Latency</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{metrics.performanceMetrics.errorCount}</p>
                    <p className="text-sm text-muted-foreground">Errors</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">{metrics.performanceMetrics.retryCount}</p>
                    <p className="text-sm text-muted-foreground">Retries</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Configuration Summary */}
      {configuration && (
        <Card>
          <CardHeader>
            <CardTitle>Configuration Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <Label>Model Provider</Label>
                <p className="capitalize">{configuration.modelProvider}</p>
              </div>
              <div>
                <Label>Model Name</Label>
                <p>{configuration.modelName}</p>
              </div>
              <div>
                <Label>Confidence Threshold</Label>
                <p>{configuration.confidenceThreshold}%</p>
              </div>
              <div>
                <Label>Last Updated</Label>
                <p>{formatDate(configuration.lastUpdated)}</p>
              </div>
              <div>
                <Label>Status</Label>
                <div className="mt-1">
                  {getStatusBadge(configuration.isActive)}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
} 